import { forwardRef } from "react";
import PropTypes from "prop-types";
import IconButton from "@mui/material/IconButton";
import { styled } from "@mui/material/styles";
import { gr } from "../tokens";

const TOUCH = "@media (pointer: coarse), (max-width: 899.95px)";

const Root = styled(IconButton, {
  shouldForwardProp: (prop) => prop !== "grVariant" && prop !== "grSize",
})(({ grVariant, grSize }) => {
  const box = grSize === "sm" ? gr.control.sm : gr.control.md;
  const bordered = grVariant !== "ghost";
  return {
    width: box,
    height: box,
    padding: 0,
    borderRadius: gr.radius.control,
    border: `1px solid ${bordered ? gr.color.line : "transparent"}`,
    background: bordered ? gr.color.surface : "transparent",
    color: gr.color.ink2,
    transition:
      "background-color .15s ease, border-color .15s ease, color .15s ease",
    "&:hover": {
      background: bordered ? gr.color.surface : gr.color.tint,
      borderColor: bordered ? gr.color.brand : "transparent",
      color: gr.color.brandDark,
    },
    "&.Mui-focusVisible": {
      boxShadow: gr.shadow.focus,
      borderColor: gr.color.brand,
    },
    "&.Mui-disabled": { color: gr.color.muted, opacity: 0.6 },
    // Open state for buttons that toggle a popover / menu (they set aria-expanded).
    '&[aria-expanded="true"]': {
      background: gr.color.brandSoft,
      borderColor: bordered ? gr.color.brand : "transparent",
      color: gr.color.brandDark,
    },
    "& svg": {
      width: grSize === "sm" ? gr.icon.sm : gr.icon.lg,
      height: grSize === "sm" ? gr.icon.sm : gr.icon.lg,
    },
    // Touch screens: md → 44, sm → 40 (comfortable tap targets).
    [TOUCH]:
      grSize !== "sm"
        ? { width: gr.control.touch, height: gr.control.touch }
        : { width: gr.control.md, height: gr.control.md },
    // soft: the one "create" action of a panel (e.g. New message +): brand-soft fill, brand-dark icon.
    ...(grVariant === "soft"
      ? {
          background: gr.color.brandSoft,
          borderColor: gr.color.brandSoft,
          color: gr.color.brandDark,
          "&:hover": {
            background: gr.color.brand,
            borderColor: gr.color.brand,
            color: gr.color.onBrand,
          },
        }
      : {}),
    // close: the × of a popup / drawer — round, no border, a soft grey fill that deepens on hover.
    ...(grVariant === "close"
      ? {
          borderRadius: gr.radius.pill,
          background: gr.color.bg,
          borderColor: "transparent",
          color: gr.color.ink2,
          "&:hover": {
            background: gr.color.line2,
            borderColor: "transparent",
            color: gr.color.ink,
          },
        }
      : {}),
  };
});

// Icon-only button. `aria-label` is required: it is the button's only text for screen readers.
const GrIconButton = forwardRef(function GrIconButton(
  { variant = "bordered", size = "md", children, ...rest },
  ref
) {
  return (
    <Root ref={ref} grVariant={variant} grSize={size} disableRipple {...rest}>
      {children}
    </Root>
  );
});

GrIconButton.propTypes = {
  "aria-label": PropTypes.string.isRequired,
  variant: PropTypes.oneOf(["bordered", "ghost", "soft", "close"]),
  size: PropTypes.oneOf(["sm", "md"]),
  children: PropTypes.node.isRequired,
};

export default GrIconButton;
